import React, { useState, type ReactElement } from "react";
import { Button, cn } from "@ndd/ui";
import type { RdScheduleMirrorState } from "../../../../shared/rd-schedule-actions";
import {
  MIRROR_FAILED_HINT,
  MIRROR_FAILED_TEXT,
  MIRROR_PENDING_HINT,
  MIRROR_PENDING_TEXT,
} from "../../lib/mirror-state";

// NO-917. The two marks a saved visit can carry while its copy in Healthie is
// not there yet: a quiet tag while it is on its way, and a calm notice with a
// Retry when it did not go through. Nothing shows for a visit that is copied
// or that NDD does not own.

/** A small muted tag for a list row or a Week card; no button, so it can sit inside a link. */
export function MirrorTag({
  mirrorState,
  className,
}: {
  mirrorState: RdScheduleMirrorState | null | undefined;
  className?: string;
}): ReactElement | null {
  if (mirrorState !== "pending" && mirrorState !== "failed") return null;

  const failed = mirrorState === "failed";

  return (
    <span
      data-mirror-state={mirrorState}
      title={failed ? MIRROR_FAILED_HINT : MIRROR_PENDING_HINT}
      className={cn(
        "inline-flex shrink-0 items-center whitespace-nowrap rounded-md border px-1.5 text-label",
        failed
          ? "border-warning-soft bg-warning-soft text-warning"
          : "border-border bg-muted text-muted-strong",
        className
      )}
    >
      {failed ? MIRROR_FAILED_TEXT : MIRROR_PENDING_TEXT}
      <span className="sr-only">{`. ${failed ? MIRROR_FAILED_HINT : MIRROR_PENDING_HINT}`}</span>
    </span>
  );
}

/** The details panel's notice: the mark, its sentence, and Retry when the copy failed. */
export function MirrorNotice({
  mirrorState,
  onRetry,
}: {
  mirrorState: RdScheduleMirrorState | null | undefined;
  /** Sends the failed copy again; absent when the visit's action is not known. */
  onRetry?: () => Promise<void>;
}): ReactElement | null {
  if (mirrorState !== "pending" && mirrorState !== "failed") return null;

  if (mirrorState === "pending") {
    return (
      <p role="status" className="text-reading text-muted-strong">
        <MirrorTag mirrorState="pending" /> {MIRROR_PENDING_HINT}
      </p>
    );
  }

  return (
    <div
      role="alert"
      className="flex flex-col gap-2 rounded-lg bg-warning-soft px-3 py-2 text-body text-warning"
    >
      <p className="font-semibold">{MIRROR_FAILED_TEXT}</p>
      <p>{MIRROR_FAILED_HINT}</p>
      {onRetry ? <RetryCopy onRetry={onRetry} /> : null}
    </div>
  );
}

function RetryCopy({ onRetry }: { onRetry: () => Promise<void> }): ReactElement {
  const [retrying, setRetrying] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);

  const retry = async (): Promise<void> => {
    setRetrying(true);
    setRetryFailed(false);

    try {
      await onRetry();
    } catch {
      setRetryFailed(true);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <>
      {retryFailed ? <p>That did not go through. Try again in a minute.</p> : null}
      <Button
        type="button"
        variant="utility"
        size="compact"
        className="self-start"
        disabled={retrying}
        onClick={() => void retry()}
      >
        {retrying ? "Retrying…" : "Retry"}
      </Button>
    </>
  );
}
